import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Field, StatusBadge, formatDateTime, inputClass, timeAgo, useAdminApi, useConfirm, useToast } from './ui';
import { HEALTH_COLORS, HEALTH_LABELS, getHealth } from './health';

const STATUS_OPTIONS = [
  ['new', 'New – inactive, awaiting approval'],
  ['active', 'Active – approved, running on schedule'],
  ['review', 'Review – under review'],
  ['paused', 'Paused – temporarily stopped'],
  ['rejected', 'Rejected – denied or too many errors'],
];
const ACTION_OPTIONS = [
  ['add', 'Full – adds/edits live listings'],
  ['suggest', 'Suggest – creates suggestions for review'],
  ['disabled', 'Disabled – temporarily inactive'],
];

const fieldsOf = (integration) => ({
  name: integration.name || '',
  status: integration.status || 'new',
  action: integration.action || 'suggest',
  frequency: integration.frequency || '',
  directory: integration.directory || '',
  url: integration.url || '',
  repo: integration.repo || '',
});

function IntegrationDrawer({ integration, reload, onClose }) {
  const api = useAdminApi();
  const toast = useToast();
  const confirm = useConfirm();

  const [form, setForm] = useState(() => fieldsOf(integration));
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [runs, setRuns] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const saved = useMemo(() => fieldsOf(integration), [integration]);
  const changed = Object.keys(form).filter((key) => form[key] !== saved[key]);
  const dirty = changed.length > 0;
  const health = getHealth(integration);
  const isScraper = integration.integrationType.name === 'Scraper';

  // After a background refresh, untouched fields pick up the new saved values; fields being edited are kept
  const savedKey = JSON.stringify(saved);
  const previousSaved = useRef(saved);
  useEffect(() => {
    setForm((current) => {
      const next = { ...current };
      Object.keys(saved).forEach((key) => {
        if (current[key] === previousSaved.current[key]) next[key] = saved[key];
      });
      return next;
    });
    previousSaved.current = saved;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const latestRunId = integration.runs && integration.runs[0] ? integration.runs[0].id : null;
  useEffect(() => {
    let cancelled = false;
    api(`/admin/integration/${integration.uuid}/runs`)
      .then((result) => { if (!cancelled) setRuns(result.runs); })
      .catch(() => { if (!cancelled) setRuns([]); });
    return () => { cancelled = true; };
  }, [api, integration.uuid, latestRunId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (key) => (e) => setForm((current) => ({ ...current, [key]: e.target.value }));

  const patch = async (fields, successMessage) => {
    const result = await api(`/admin/integration/${integration.uuid}`, { method: 'PATCH', body: fields });
    toast(successMessage || result.message);
    await reload({ silent: true });
  };

  const save = async () => {
    setSaving(true);
    try {
      const fields = {};
      changed.forEach((key) => { fields[key] = form[key]; });
      await patch(fields, 'Saved.');
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const quickStatus = async (status, label) => {
    setBusy(status);
    try {
      await patch({ status }, label);
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setBusy('');
    }
  };

  const queueRun = async () => {
    const ok = await confirm({
      title: 'Run this integration now?',
      message: integration.status === 'active'
        ? 'It will be queued and write results to the live listings according to its access level.'
        : `Status is "${integration.status}". The run is queued anyway and will write according to its access level (${integration.action}).`,
      confirmLabel: 'Queue run',
    });
    if (!ok) return;
    setBusy('run');
    try {
      const result = await api(`/admin/integration/${integration.uuid}/queue-run`, { method: 'POST' });
      toast(result.message);
      setTimeout(() => reload({ silent: true }), 15000);
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setBusy('');
    }
  };

  const testRun = async () => {
    setBusy('test');
    setTestResult(null);
    try {
      setTestResult(await api(`/admin/integration/${integration.uuid}/test`, { method: 'POST' }));
    } catch (error) {
      setTestResult({ ok: false, stage: 'request', message: error.message });
    } finally {
      setBusy('');
    }
  };

  const importCode = async () => {
    if (dirty && changed.includes('repo')) {
      toast('Save the repository URL first.', 'error');
      return;
    }
    setBusy('import');
    try {
      const result = await api(`/admin/integration/${integration.uuid}/import-code`, { method: 'POST' });
      toast(result.message);
      await reload({ silent: true });
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setBusy('');
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: 'Delete this integration?',
      message: 'It stops running and disappears from the admin. Run history is kept in the database.',
      confirmLabel: 'Delete integration',
      danger: true,
      requireText: 'delete',
    });
    if (!ok) return;
    setBusy('delete');
    try {
      const result = await api(`/admin/integration/${integration.uuid}`, { method: 'DELETE' });
      toast(result.message);
      onClose();
      await reload({ silent: true });
    } catch (error) {
      toast(error.message, 'error');
      setBusy('');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-[60]" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-[61] overflow-y-auto" role="dialog" aria-label="Integration details">
        <header className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between gap-4 z-10">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold truncate">{integration.place.name}</h2>
            <div className="text-sm text-gray-600 flex flex-wrap items-center gap-2 mt-1">
              <span>{integration.place.region.name}</span>
              <span>· {integration.integrationType.name}</span>
              <StatusBadge status={integration.status} />
              <Badge color={HEALTH_COLORS[health.key]}>{HEALTH_LABELS[health.key]}</Badge>
            </div>
            {health.reasons.length > 0 && health.key !== 'ok' && <p className="text-xs text-gray-500 mt-1">{health.reasons.join(' · ')}</p>}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-black text-xl leading-none" aria-label="Close">✕</button>
        </header>

        <div className="p-6 space-y-6">
          <div className="flex flex-wrap gap-2">
            {(integration.status === 'new' || integration.status === 'review') && (
              <Button variant="success" busy={busy === 'active'} onClick={() => quickStatus('active', 'Approved and activated.')}>Approve &amp; activate</Button>
            )}
            {integration.status === 'active' && <Button busy={busy === 'paused'} onClick={() => quickStatus('paused', 'Paused.')}>Pause</Button>}
            {(integration.status === 'paused' || integration.status === 'rejected') && (
              <Button busy={busy === 'active'} onClick={() => quickStatus('active', 'Resumed.')}>Resume</Button>
            )}
            <Button variant="primary" busy={busy === 'run'} onClick={queueRun}>Run now</Button>
            {isScraper && <Button busy={busy === 'test'} onClick={testRun}>Test run (no changes)</Button>}
            <Link
              target="_blank"
              to={`/region/${integration.place.region.urlName}/place/${integration.place.urlName}/edit/integrations`}
              className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
            >
              Place integrations page ↗
            </Link>
          </div>

          {busy === 'test' && <p className="text-sm text-gray-600">Running the scraper… this can take a minute for browser-based scrapers. Nothing is saved.</p>}
          {testResult && <TestResult result={testResult} onDismiss={() => setTestResult(null)} />}

          <section className="space-y-4">
            <h3 className="font-semibold">Settings</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Label"><input className={inputClass} value={form.name} onChange={set('name')} /></Field>
              <Field label="Status">
                <select className={inputClass} value={form.status} onChange={set('status')}>
                  {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
              <Field label="Access level">
                <select className={inputClass} value={form.action} onChange={set('action')}>
                  {ACTION_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
              <Field label="Frequency">
                <select className={inputClass} value={form.frequency} onChange={set('frequency')}>
                  <option value="">No schedule (manual only)</option>
                  <option value="daily">Daily – every night</option>
                  <option value="weekly">Weekly</option>
                </select>
              </Field>
              {isScraper && (
                <Field
                  label="Scraper file"
                  hint={`cronjobs/scrapers/${integration.place.region.urlName}/${form.directory || '…'}.js${integration.scraperFileFound === false ? ' — not found on the server' : ''}`}
                >
                  <input className={`${inputClass} ${integration.scraperFileFound === false ? 'border-red-400' : ''}`} value={form.directory} onChange={set('directory')} placeholder="e.g. whammyScraper" />
                </Field>
              )}
              {integration.integrationType.name !== 'Eventbrite' && integration.integrationType.name !== 'TMDB' && (
                <Field label="Source URL"><input className={inputClass} value={form.url} onChange={set('url')} placeholder="https://…" /></Field>
              )}
            </div>
            {isScraper && (
              <Field label="Git repository" hint="https:// or git@ URL. “Import code” clones it into the scraper directory, or pulls updates if already cloned.">
                <div className="flex gap-2">
                  <input className={inputClass} value={form.repo} onChange={set('repo')} placeholder="https://github.com/…" />
                  <Button busy={busy === 'import'} disabled={!saved.repo} onClick={importCode}>Import code</Button>
                </div>
              </Field>
            )}
            <div className="flex items-center gap-3">
              <Button variant="primary" busy={saving} disabled={!dirty} onClick={save}>Save changes</Button>
              {dirty && <Button onClick={() => setForm(saved)}>Discard</Button>}
              {dirty && <span className="text-sm text-amber-700">Unsaved: {changed.join(', ')}</span>}
            </div>
          </section>

          <section>
            <h3 className="font-semibold mb-2">Run history</h3>
            <RunHistory runs={runs} />
          </section>

          <section className="border-t border-gray-200 pt-4">
            <Button variant="danger" busy={busy === 'delete'} onClick={remove}>Delete integration</Button>
            <span className="text-xs text-gray-500 ml-3">Added {formatDateTime(integration.createdAt)}</span>
          </section>
        </div>
      </aside>
    </>
  );
}

function RunHistory({ runs }) {
  const [open, setOpen] = useState(null);
  if (runs === null) return <p className="text-sm text-gray-500">Loading…</p>;
  if (runs.length === 0) return <p className="text-sm text-gray-500">No runs yet.</p>;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-left">
          <tr>
            <th className="px-3 py-2 font-medium">When</th>
            <th className="px-3 py-2 font-medium">Result</th>
            <th className="px-3 py-2 font-medium text-right">Added</th>
            <th className="px-3 py-2 font-medium text-right">Updated</th>
            <th className="px-3 py-2 font-medium text-right">Errors</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {runs.map((run) => {
            const errorCount = run.errorLogs.length;
            return (
              <React.Fragment key={run.uuid}>
                <tr className={errorCount ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => errorCount && setOpen(open === run.uuid ? null : run.uuid)}>
                  <td className="px-3 py-2 whitespace-nowrap" title={formatDateTime(run.createdAt)}>{timeAgo(run.createdAt)}</td>
                  <td className="px-3 py-2">{run.status === 'finished' ? <span className="text-green-700">finished</span> : <span className="text-amber-700">{run.status || '—'}</span>}</td>
                  <td className="px-3 py-2 text-right">{run.added ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{run.updated ?? '—'}</td>
                  <td className={`px-3 py-2 text-right ${errorCount ? 'text-red-700 font-medium' : 'text-gray-400'}`}>{errorCount ? `${errorCount} ▾` : '0'}</td>
                </tr>
                {open === run.uuid && (
                  <tr>
                    <td colSpan="5" className="bg-red-50 px-3 py-2">
                      <ul className="space-y-2">
                        {run.errorLogs.map((log) => (
                          <li key={log.id} className="text-xs">
                            <div className="font-medium text-red-800">{[log.type, log.message || log.comment].filter(Boolean).join(': ')}</div>
                            {log.details && <pre className="mt-1 whitespace-pre-wrap text-gray-700 max-h-40 overflow-auto">{log.details}</pre>}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TestResult({ result, onDismiss }) {
  if (!result.ok) {
    return (
      <div className="border border-red-300 bg-red-50 rounded-lg p-4 text-sm">
        <div className="flex justify-between items-start">
          <div className="font-semibold text-red-800">Test failed{result.stage ? ` (${result.stage})` : ''}</div>
          <button onClick={onDismiss} className="text-gray-500 hover:text-black">✕</button>
        </div>
        <p className="mt-1 text-red-900">{result.message}</p>
        {result.stack && <pre className="mt-2 text-xs whitespace-pre-wrap text-gray-700 max-h-40 overflow-auto">{result.stack}</pre>}
      </div>
    );
  }

  const { summary = {}, warnings = [], sample = [], types = {} } = result;
  return (
    <div className="border border-green-300 bg-green-50 rounded-lg p-4 text-sm space-y-3">
      <div className="flex justify-between items-start">
        <div className="font-semibold text-green-900">
          Found {result.count} listing{result.count === 1 ? '' : 's'} in {(result.durationMs / 1000).toFixed(1)}s — nothing was saved
        </div>
        <button onClick={onDismiss} className="text-gray-500 hover:text-black">✕</button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge color="green">{summary.upcoming || 0} upcoming</Badge>
        <Badge color="gray">{summary.past || 0} past</Badge>
        {Object.entries(types).map(([type, count]) => <Badge key={type} color="blue">{type} × {count}</Badge>)}
        {result.firstDate && <Badge color="gray">{formatDateTime(result.firstDate)} → {formatDateTime(result.lastDate)}</Badge>}
      </div>

      {warnings.length > 0 && (
        <ul className="bg-amber-50 border border-amber-200 rounded p-2 text-amber-900 list-disc list-inside">
          {warnings.map((warning) => <li key={warning}>{warning}</li>)}
        </ul>
      )}

      {sample.length > 0 && (
        <div className="space-y-2">
          <div className="font-medium">First {sample.length}</div>
          {sample.map((item, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded p-2 flex gap-3">
              {item.image && <img src={item.image} alt="" className="w-14 h-14 object-cover rounded flex-shrink-0" />}
              <div className="min-w-0">
                <div className="font-medium truncate">{item.name || <span className="text-red-600">(no name)</span>}</div>
                <div className="text-xs text-gray-600">{item.type} · {item.startDate ? formatDateTime(item.startDate) : <span className="text-red-600">no start date</span>}{item.location ? ` · ${typeof item.location === 'string' ? item.location : ''}` : ''}</div>
                {item.url && <a href={item.url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline truncate block">{item.url}</a>}
              </div>
            </div>
          ))}
          <details>
            <summary className="cursor-pointer text-xs text-gray-600">Raw JSON</summary>
            <pre className="mt-1 text-xs bg-white border border-gray-200 rounded p-2 max-h-64 overflow-auto">{JSON.stringify(sample, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

export default IntegrationDrawer;
